import { useEffect, useRef, useState } from 'react';

/**
 * Marks an element once it has scrolled into view, and keeps it marked.
 *
 * The site's entrance animations (.animate-fade-up and friends) are plain CSS
 * animations, so they run the moment the page loads — which means everything
 * below the fold has already finished animating by the time you scroll to it.
 * This hook is the gate: the section holds its animations at frame zero until
 * it is genuinely on screen.
 *
 * It fires ONCE and then disconnects. A reveal that replays every time you
 * scroll past turns a landing page into a flicker, and re-observing forever
 * costs work on every scroll for an effect that can only happen once.
 */
const useInView = ({ threshold = 0.15, rootMargin = '0px 0px -10% 0px' } = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    /* No IntersectionObserver (old Safari, some test environments) means no
       gate — show the content rather than leaving the whole page invisible. */
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          setInView(true);
          observer.disconnect();
        });
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [ref, inView];
};

export default useInView;
